import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  Home,
  Loader2,
  Save,
  ShieldCheck,
} from "lucide-react";
import request from "../utils/request";
import { resolveHostAccountId } from "../utils/hostAccount";

function propertyLabel(property) {
  return property?.listingTitle || property?.address?.line1 || property?.propertyUniqueId || "Property";
}

export default function GuidebookFieldsScreen() {
  const { hostInfo } = useSelector((state) => state.host);
  const hostDetails = useSelector((state) => state.hostDetails?.Info);
  const hostId = resolveHostAccountId(hostInfo);

  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [fields, setFields] = useState([]);
  const [serverSetupEnabled, setServerSetupEnabled] = useState(
    hostInfo?.safeFieldsSetupEnabled === true || hostDetails?.safeFieldsSetupEnabled === true,
  );
  const [loading, setLoading] = useState(true);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!hostId) {
      setError("Unable to determine your host account.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    request
      .get(`/hosts/${hostId}/guidebook-fields/properties`)
      .then(({ data }) => {
        setServerSetupEnabled(data?.setupEnabled === true);
        const rows = data?.properties || [];
        setProperties(rows);
        setSelectedPropertyId((current) => (
          current && rows.some((property) => property.propertyUniqueId === current)
            ? current
            : rows[0]?.propertyUniqueId || ""
        ));
      })
      .catch((err) => {
        setProperties([]);
        setServerSetupEnabled(false);
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Guidebook field setup is not enabled for this host."
        );
      })
      .finally(() => setLoading(false));
  }, [hostId]);

  useEffect(() => {
    if (!hostId || !selectedPropertyId) {
      setFields([]);
      return;
    }
    setFieldsLoading(true);
    setError("");
    request
      .get(`/hosts/${hostId}/guidebook-fields`, {
        params: { propertyUniqueId: selectedPropertyId },
      })
      .then(({ data }) => setFields(data?.fields || []))
      .catch((err) => {
        setFields([]);
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Unable to load guidebook fields."
        );
      })
      .finally(() => setFieldsLoading(false));
  }, [hostId, selectedPropertyId]);

  const selectedProperty = useMemo(
    () => properties.find((property) => property.propertyUniqueId === selectedPropertyId) || null,
    [properties, selectedPropertyId],
  );

  const enabledCount = fields.filter((field) => field.enabled).length;

  const updateField = (targetField, patch) => {
    setSaved(false);
    setFields((current) => current.map((field) => (
      field.targetField === targetField ? { ...field, ...patch } : field
    )));
  };

  const saveFields = async () => {
    if (!hostId || !selectedPropertyId) return;
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const { data } = await request.patch(`/hosts/${hostId}/guidebook-fields`, {
        propertyUniqueId: selectedPropertyId,
        fields: fields.map((field) => ({
          targetField: field.targetField,
          value: field.manualValue || "",
          enabled: field.enabled === true,
        })),
      });
      setFields(data?.fields || []);
      setSaved(true);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to save guidebook fields."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#EF4949]" />
      </div>
    );
  }

  if (!serverSetupEnabled || error.includes("not enabled")) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] px-6 py-10">
        <div className="max-w-3xl mx-auto rounded-[16px] border border-gray-200 bg-white p-8 shadow-sm">
          <ShieldCheck className="w-8 h-8 text-gray-400 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Guidebook field setup is off</h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            Bellhopt support must enable this host account before property guidebook fields can be configured.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] px-6 py-8">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#ECFDF5] px-3 py-1 text-[12px] font-bold text-[#047857] border border-[#D1FAE5]">
              <ShieldCheck className="w-3.5 h-3.5" />
              Admin enabled
            </div>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-950">Guidebook fields</h1>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Configure the safe property details guests can see in the mobile guidebook.
            </p>
          </div>

          <button
            onClick={saveFields}
            disabled={saving || fieldsLoading || !selectedPropertyId}
            className="inline-flex items-center justify-center gap-2 rounded-[12px] bg-gray-950 px-4 py-3 text-sm font-bold text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>

        {error && !error.includes("not enabled") && (
          <div className="mb-5 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}

        {saved && (
          <div className="mb-5 flex items-center gap-2 rounded-[14px] border border-[#D1FAE5] bg-[#ECFDF5] px-4 py-3 text-sm font-semibold text-[#047857]">
            <CheckCircle2 className="w-4 h-4" />
            Guidebook fields saved.
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="rounded-[16px] border border-gray-200 bg-white p-3 shadow-sm h-fit">
            <div className="px-2 pb-3">
              <p className="text-[12px] font-bold uppercase text-gray-400">Properties</p>
            </div>
            <div className="space-y-1">
              {properties.map((property) => (
                <button
                  key={property.propertyUniqueId}
                  type="button"
                  onClick={() => setSelectedPropertyId(property.propertyUniqueId)}
                  className={`w-full text-left rounded-[12px] px-3 py-3 transition ${
                    selectedPropertyId === property.propertyUniqueId
                      ? "bg-gray-950 text-white"
                      : "hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <Home className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold truncate">{propertyLabel(property)}</p>
                      <p className={`text-[12px] truncate ${
                        selectedPropertyId === property.propertyUniqueId ? "text-gray-300" : "text-gray-400"
                      }`}>
                        {property.propertyUniqueId}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          <section className="rounded-[16px] border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-5 py-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-950">{propertyLabel(selectedProperty)}</h2>
                <p className="text-[12px] text-gray-500">{enabledCount} fields enabled for guest-mobile</p>
              </div>
            </div>

            {fieldsLoading ? (
              <div className="py-20 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : fields.length === 0 ? (
              <div className="px-5 py-16 text-center text-sm text-gray-500">
                No eligible safe guidebook fields are available for this property.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {fields.map((field) => (
                  <div key={field.targetField} className="p-5">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-gray-950">{field.displayLabel}</h3>
                        <p className="mt-1 text-[12px] text-gray-400">{field.targetField}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateField(field.targetField, { enabled: !field.enabled })}
                        className={`inline-flex items-center gap-2 rounded-[10px] px-3 py-2 text-[12px] font-bold border ${
                          field.enabled
                            ? "bg-[#ECFDF5] text-[#047857] border-[#D1FAE5]"
                            : "bg-gray-50 text-gray-500 border-gray-200"
                        }`}
                      >
                        {field.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        {field.enabled ? "Visible" : "Hidden"}
                      </button>
                    </div>

                    {field.sourceValue ? (
                      <div className="mt-4 rounded-[12px] bg-gray-50 border border-gray-100 px-3 py-2">
                        <p className="text-[11px] font-bold uppercase text-gray-400 mb-1">Current safe source</p>
                        <p className="whitespace-pre-wrap text-sm text-gray-700">{field.sourceValue}</p>
                      </div>
                    ) : null}

                    <label className="mt-4 block">
                      <span className="text-[12px] font-bold text-gray-500">Manual value</span>
                      <textarea
                        value={field.manualValue || ""}
                        onChange={(event) => updateField(field.targetField, { manualValue: event.target.value })}
                        rows={3}
                        className="mt-2 w-full rounded-[12px] border border-gray-200 bg-white px-3 py-3 text-sm text-gray-900 outline-none focus:border-gray-400"
                        placeholder="Leave blank to use the safe PMS value when available."
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
